import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { composerCanSend, composerDraftAfterDictation, composerSendsOnKey, boundedComposerText } from '../lib/composer-behavior';
import { ComposerHistory, composerHistoryDirection } from '../lib/composer-history';
import { DictateButton } from './Voice';
import '../conversational-composer.css';

export interface ConversationalComposerProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSend: (value: string) => void;
  readonly label: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly busy?: boolean;
  /** Disable only the send action while keeping draft editing and dictation available. */
  readonly sendDisabled?: boolean;
  readonly maxLength?: number;
  readonly sendLabel?: string;
  readonly helperText?: ReactNode;
  readonly dictateLang?: string;
  /** In-memory recall boundary, such as one organization + Worker + PTY session. */
  readonly historyScope?: string;
}

export function ConversationalComposer({
  value,
  onChange,
  onSend,
  label,
  placeholder,
  disabled = false,
  busy = false,
  sendDisabled = false,
  maxLength,
  sendLabel = 'Send',
  helperText,
  dictateLang,
  historyScope,
}: ConversationalComposerProps) {
  const helperId = useId();
  const box = useRef<HTMLTextAreaElement | null>(null);
  const history = useRef(new ComposerHistory());
  const locked = disabled || busy;
  const sendable = !sendDisabled && composerCanSend(value, { disabled, busy, maxLength });

  const send = () => {
    if (!sendable) return;
    if (historyScope !== undefined) history.current.record(historyScope, value);
    onSend(value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (historyScope !== undefined) {
      const direction = composerHistoryDirection(
        {
          key: event.key,
          shiftKey: event.shiftKey,
          isComposing: event.nativeEvent.isComposing,
          altKey: event.altKey,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
        },
        event.currentTarget.selectionStart,
        event.currentTarget.selectionEnd,
        value.length,
        history.current.isWalking(historyScope),
      );
      if (direction !== null) {
        const recalled = history.current.recall(historyScope, direction, value);
        if (recalled !== null) {
          const next = boundedComposerText(recalled, maxLength);
          event.preventDefault();
          onChange(next);
          queueMicrotask(() => box.current?.setSelectionRange(next.length, next.length));
          return;
        }
      }
    }
    if (sendDisabled) return;
    if (!composerSendsOnKey({
      key: event.key,
      shiftKey: event.shiftKey,
      isComposing: event.nativeEvent.isComposing,
      altKey: event.altKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
    }, value, { disabled, busy, maxLength })) return;
    event.preventDefault();
    send();
  };

  return (
    <fieldset className="conversational-composer" disabled={locked} aria-busy={busy || undefined}>
      <textarea
        ref={box}
        className="command-box"
        aria-label={label}
        aria-describedby={helperText === undefined ? undefined : helperId}
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        onChange={(event) => {
          if (historyScope !== undefined) history.current.edit(historyScope);
          onChange(boundedComposerText(event.target.value, maxLength));
        }}
        onKeyDown={onKeyDown}
      />
      <div className="conversational-composer-footer">
        <span className="conversational-composer-helper muted" id={helperText === undefined ? undefined : helperId}>
          {helperText}
        </span>
        <span className="row conversational-composer-actions">
          <DictateButton
            lang={dictateLang}
            onText={(text) => {
              if (historyScope !== undefined) history.current.edit(historyScope);
              onChange(composerDraftAfterDictation(value, text, maxLength));
            }}
          />
          <button type="button" className="primary" disabled={!sendable} onClick={send}>
            {sendLabel}
          </button>
        </span>
      </div>
    </fieldset>
  );
}
